import { Shield, Zap, Code, Wind, Layers, Server, MessageSquare, BarChart3, Sparkles, AlertTriangle, Smile } from "lucide-react"

export function NextTailwindFeaturesPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Key Features</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          The Next.js + Tailwind CSS template comes packed with production-ready features including 
          server-side rendering, utility-first CSS, advanced UI components, and modern development tools.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <FeatureCard
          icon={<MessageSquare className="h-8 w-8 text-primary" />}
          title="Complete Chat Application"
          items={[
            "Direct & group chat conversations",
            "Message reactions with emoji picker",
            "Pin important messages",
            "File attachments with upload support",
            "Real-time online/offline status indicators",
            "Unread message badges",
            "Search conversations",
            "Add users to group chats",
          ]}
        />

        <FeatureCard
          icon={<BarChart3 className="h-8 w-8 text-primary" />}
          title="Admin Dashboard Components"
          items={[
            "MetricCard with trend indicators",
            "DataTable with search & sorting",
            "ChartCard with bar & line charts",
            "ActivityFeed with timeline",
            "QuickActions button grid",
            "ProgressBar with variants",
            "Badge with status colors",
            "SimpleChart visualizations",
          ]}
        />

        <FeatureCard
          icon={<Sparkles className="h-8 w-8 text-primary" />}
          title="Enhanced UX Features"
          items={[
            "Resizable sidebar with drag handle",
            "Enhanced header with search & notifications",
            "Smooth animations and transitions",
            "Backdrop blur effects",
            "Hover states on all interactive elements",
            "Mobile-first responsive design",
            "Theme toggle with rotation animation",
            "User profile with online status",
          ]}
        />

        <FeatureCard
          icon={<AlertTriangle className="h-8 w-8 text-primary" />}
          title="Error Handling & Auth"
          items={[
            "Error boundary for crash handling",
            "Custom 404 not found page",
            "Login page with validation",
            "Signup page with password matching",
            "Global confirmation dialog",
            "Global loader with nested states",
            "Built-in error.tsx pages",
            "Error pages with recovery options",
          ]}
        />

        <FeatureCard
          icon={<Smile className="h-8 w-8 text-primary" />}
          title="Global Components"
          items={[
            "EmojiPicker with emoji-mart integration",
            "Modal/Dialog with multiple sizes",
            "Confirmation dialog with variants",
            "Loading spinner with messages",
            "Theme-aware components",
            "Keyboard shortcuts (ESC, Enter)",
            "Click-outside to close",
            "Accessible ARIA labels",
          ]}
        />
        
        <FeatureCard
          icon={<Zap className="h-8 w-8 text-primary" />}
          title="Next.js 15 App Router"
          items={[
            "File-based routing with layouts",
            "Server and Client Components",
            "Server Actions for mutations",
            "Streaming with Suspense boundaries",
            "Route Handlers for API endpoints",
            "Parallel and intercepting routes",
          ]}
        />
        
        <FeatureCard
          icon={<Wind className="h-8 w-8 text-primary" />}
          title="Tailwind CSS Utility-First"
          items={[
            "Utility-first CSS for rapid development",
            "JIT compiler for instant style generation",
            "PurgeCSS for minimal production bundle",
            "Custom design system with theme variables",
            "Dark mode with class-based switching",
            "Responsive utilities for all breakpoints",
          ]}
        />
        
        <FeatureCard
          icon={<Server className="h-8 w-8 text-primary" />}
          title="Rendering Strategies"
          items={[
            "Server-side rendering (SSR) by default",
            "Static Site Generation (SSG)",
            "Incremental Static Regeneration (ISR)",
            "Client-side rendering when needed",
            "Automatic code splitting",
            "Optimized bundle size",
          ]}
        />
        
        <FeatureCard
          icon={<Code className="h-8 w-8 text-primary" />}
          title="TypeScript & Development"
          items={[
            "Full TypeScript support with strict mode",
            "Type-safe API routes and server actions",
            "Auto-completion and IntelliSense",
            "Path aliases (@/) for clean imports",
            "ESLint configuration for code quality",
            "Fast refresh for instant feedback",
          ]}
        />
        
        <FeatureCard
          icon={<Layers className="h-8 w-8 text-primary" />}
          title="Clean Architecture"
          items={[
            "App Router with nested layouts",
            "Loading and error boundaries",
            "Reusable component patterns",
            "Custom hooks for client logic",
            "Centralized constants and types",
            "Utility functions for common operations",
          ]}
        />
        
        <FeatureCard
          icon={<Shield className="h-8 w-8 text-primary" />}
          title="Performance & SEO"
          items={[
            "Optimized images with next/image",
            "Font optimization with next/font",
            "Metadata API for SEO",
            "OpenGraph and Twitter cards",
            "Sample Product service with full CRUD",
            "Automatic static optimization",
          ]}
        />
      </div>

      <div className="bg-muted/30 rounded-xl p-8 space-y-6 mt-12">
        <h2 className="text-2xl font-bold">Why Choose Next.js + Tailwind CSS?</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Full-Stack Capabilities</h3>
            <p className="text-muted-foreground leading-relaxed">
              Next.js provides everything you need to build a complete application - from server 
              rendering to API routes. No need for a separate backend for many use cases.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Maximum Flexibility</h3>
            <p className="text-muted-foreground leading-relaxed">
              Tailwind's utility-first approach gives you complete control over your designs 
              without the constraints of pre-built components. Style exactly what you need.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">SEO & Performance</h3>
            <p className="text-muted-foreground leading-relaxed">
              Server-side rendering ensures your pages are SEO-friendly and load instantly. 
              PurgeCSS removes unused styles for incredibly small file sizes.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Production Ready</h3>
            <p className="text-muted-foreground leading-relaxed">
              Deploy anywhere - Vercel, AWS, Docker containers. Built-in optimizations and 
              best practices ensure your app is ready for production from day one.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-card border rounded-xl p-8 space-y-6">
        <h2 className="text-2xl font-bold">Component Library</h2>
        <p className="text-muted-foreground leading-relaxed">
          The template comes with a comprehensive set of custom-built components using pure Tailwind CSS, 
          plus dashboard, chat, and global utility components.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <SuperpowerCard
            title="Custom UI Components"
            items={["Button", "Card", "Input", "Modal", "Avatar", "Badge", "Switch", "Spinner"]}
          />
          <SuperpowerCard
            title="Dashboard Components"
            items={["MetricCard", "DataTable", "ChartCard", "SimpleChart", "ActivityFeed", "QuickActions", "ProgressBar"]}
          />
          <SuperpowerCard
            title="Chat Components"
            items={["ChatList", "ChatMessage", "ChatInput", "PinnedMessage", "MessageReactions", "AddUserModal"]}
          />
          <SuperpowerCard
            title="Global Components"
            items={["EmojiPicker", "Modal", "ConfirmationDialog", "GlobalLoader", "ErrorBoundary"]}
          />
          <SuperpowerCard
            title="Layout"
            items={["AppLayout", "Sidebar (Resizable)", "Header (Enhanced)", "AuthLayout"]}
          />
          <SuperpowerCard
            title="Pages"
            items={["Dashboard", "Chat", "Users", "Analytics", "Settings", "Login", "Signup", "NotFound"]}
          />
        </div>
      </div>

      <div className="bg-primary/5 border-l-4 border-primary rounded-r-xl p-8 space-y-4 mt-8">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          New in This Update
        </h2>
        <div className="space-y-4 text-muted-foreground leading-relaxed">
          <p>
            <strong className="text-foreground">Complete Chat System:</strong> A full-featured chat application with direct 
            and group conversations, message reactions, pinned messages, file attachments, and an emoji picker. All components 
            properly marked with 'use client' directives for Next.js.
          </p>
          <p>
            <strong className="text-foreground">Dashboard Components:</strong> Professional admin dashboard components including 
            metric cards with trends, sortable data tables, charts, activity feeds, and progress bars.
          </p>
          <p>
            <strong className="text-foreground">Enhanced UX:</strong> Resizable sidebar with persistent width, enhanced header 
            with search and notifications, smooth animations, and improved mobile responsiveness.
          </p>
          <p>
            <strong className="text-foreground">Production Features:</strong> Error boundaries, 404 pages, authentication pages, 
            global confirmation dialogs, loading states, and a complete Product API service example.
          </p>
        </div>
      </div>
    </div>
  )
}

function FeatureCard({ 
  icon, 
  title, 
  items 
}: { 
  icon: React.ReactNode
  title: string
  items: string[]
}) {
  return (
    <div className="bg-card border rounded-xl p-6 space-y-4">
      <div className="flex items-center gap-3">
        {icon}
        <h3 className="text-xl font-semibold">{title}</h3>
      </div>
      <ul className="space-y-3">
        {items.map((item, index) => (
          <li key={index} className="flex gap-3 text-sm text-muted-foreground">
            <span className="text-primary shrink-0">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SuperpowerCard({ 
  title, 
  items 
}: { 
  title: string
  items: string[]
}) {
  return (
    <div className="bg-muted/30 rounded-lg p-4 space-y-3">
      <h4 className="font-semibold">{title}</h4>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="text-sm text-muted-foreground flex gap-2">
            <span className="text-primary">→</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
